import { Select as SuiSelect } from '@tempo-io/sui-select';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type OptionType = { value: string | number; label: string };

type LimitTagsOverlayProps = {
  selectedOptions: OptionType[];
  placeholder?: string;
};

const LimitTagsOverlay = ({ selectedOptions, placeholder }: LimitTagsOverlayProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div
        className="absolute inset-y-0 left-3 flex items-center gap-1 pointer-events-none"
        aria-hidden="true"
      >
        {selectedOptions.length === 0 ? (
          <span className="text-sm text-text-neutral-secondary">{placeholder}</span>
        ) : (
          <>
            <span className="inline-flex items-center bg-bg-brand-subtle text-text-brand-primary rounded-md px-2 py-1 text-xs">
              {selectedOptions[0]?.label ?? ''}
            </span>
            {selectedOptions.length > 1 && (
              <span className="text-sm text-text-neutral-primary">
                +{selectedOptions.length - 1}
              </span>
            )}
          </>
        )}
      </div>
      {selectedOptions.length > 0 && (
        <span className="sr-only">
          {selectedOptions.map((o) => o.label).join(', ')} {t('common.selected')}
        </span>
      )}
    </>
  );
};

type MultiSelectProps = {
  menuItems?: OptionType[];
  placeholder?: string;
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  isOptionSelected?: (option: OptionType) => boolean;
  limitTags?: boolean;
  isDisabled?: boolean;
};

export const MultiSelect = ({
  menuItems,
  placeholder,
  value,
  onChange,
  isOptionSelected,
  limitTags,
  isDisabled,
}: MultiSelectProps) => {
  const selectedOptions = menuItems?.filter((opt) => value?.includes(opt.value)) ?? [];
  const [isActive, setIsActive] = useState(false);
  const showOverlay = limitTags && !isActive;

  const setInactive = () => setIsActive(false);
  const setActive = () => setIsActive(true);

  return (
    <div className={showOverlay ? 'relative' : undefined}>
      <SuiSelect<OptionType, true>
        isMulti
        isClearable={false}
        options={menuItems}
        value={selectedOptions}
        onChange={(opts) => onChange?.((opts ?? []).map((o) => o.value))}
        placeholder={showOverlay ? '' : placeholder}
        isDisabled={isDisabled}
        isOptionSelected={isOptionSelected}
        controlShouldRenderValue={!showOverlay}
        onFocus={setActive}
        onBlur={setInactive}
        onMenuOpen={setActive}
      />
      {showOverlay && (
        <LimitTagsOverlay selectedOptions={selectedOptions} placeholder={placeholder} />
      )}
    </div>
  );
};
